import {
    collection,
    serverTimestamp,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    getDocs,
    limit,
  } from "firebase/firestore";
  import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject,
  } from "firebase/storage";
  import { db, storage, auth } from "../firebase/config";
  import { UserProfile } from "../types/user";
  import { TalentData, TalentFormData, TalentType } from "../types/talent";
  import { PromoterData, PromoterFormData } from "../types/promoter";
  import { LocationService } from "./locationService";
  import { MediaItem, MediaUpload } from "../types/media";
  import { getFirestore } from "firebase/firestore";
  
  export class ProfileServiceError extends Error {
    constructor(
      message: string,
      public code:
        | "AUTH_REQUIRED"
        | "MISSING_PROFILE_DATA"
        | "MISSING_REQUIRED_FIELDS"
        | "IMAGE_UPLOAD_ERROR"
        | "PROFILE_NOT_FOUND"
        | "INVALID_FILE_TYPE"
        | "FILE_TOO_LARGE"
        | "UPDATE_FAILED"
        | "DELETE_FAILED"
        | "QUERY_FAILED"
        | "GALLERY_UPDATE_FAILED"
        | "INVALID_PROFILE_TYPE"
        | "UNAUTHORIZED",
      public originalError?: unknown
    ) {
      super(message);
      this.name = "ProfileServiceError";
    }
  }
  
  export class ProfileService {
    private static readonly COLLECTION = "users";
    private static readonly GALLERY_PATH = "galleries";
    private static readonly MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  
    private static validateAuth(): string {
      const currentUser = auth.currentUser;
      console.log('Current auth state:', { 
        isAuthenticated: !!currentUser,
        uid: currentUser?.uid,
        email: currentUser?.email 
      });
      
      if (!currentUser) {
        throw new ProfileServiceError(
          "No authenticated user found",
          "AUTH_REQUIRED"
        );
      }
      return currentUser.uid;
    }

    private static getStoragePath(type: "talent" | "promoter"): string {
        return type === "talent" ? "talents" : "promoters";
      }
  
    private static async handleImageUpload(
      userId: string,
      image: File,
      type: "talent" | "promoter",
      oldImageUrl?: string
    ): Promise<string> {
      try {
        if (!image.type.startsWith("image/")) {
          throw new ProfileServiceError(
            "File must be an image",
            "INVALID_FILE_TYPE"
          );
        }
  
        if (image.size > this.MAX_IMAGE_SIZE) {
          throw new ProfileServiceError(
            "Image size must be less than 5MB",
            "FILE_TOO_LARGE"
          );
        }
  
        // Delete old image if it exists
        if (oldImageUrl) {
          try {
            const oldImageRef = ref(storage, oldImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.warn("Failed to delete old image:", error);
          }
        }
  
        const storagePath = this.getStoragePath(type);
        const fileName = `${storagePath}/${userId}/${Date.now()}-${encodeURIComponent(
          image.name
        )}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, image);
        return await getDownloadURL(storageRef);
      } catch (error) {
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to handle image upload",
          "IMAGE_UPLOAD_ERROR",
          error
        );
      }
    }
  
    private static validateTalentFormData(data: Partial<TalentFormData>): void {
      const requiredFields = ['name', 'talentType'] as const;
      const missingFields = requiredFields.filter(field => 
        data[field] === undefined || data[field] === ''
      );
  
      if (missingFields.length > 0) {
        throw new ProfileServiceError(
          `Missing required fields: ${missingFields.join(", ")}`,
          "MISSING_REQUIRED_FIELDS"
        );
      }
  
      if (!data.city || !data.country) {
        throw new ProfileServiceError(
          "Location (city and country) is required",
          "MISSING_REQUIRED_FIELDS"
        );
      }
    }
  
    private static validatePromoterFormData(data: Partial<PromoterFormData>): void {
      console.log('Validating promoter form data:', data);
      
      const requiredFields = ['promotionName', 'ownerName', 'showFrequency'] as const;
      const missingFields = requiredFields.filter(field => 
        data[field] === undefined || data[field] === ''
      );
  
      console.log('Missing fields:', missingFields);
  
      if (missingFields.length > 0) {
        throw new ProfileServiceError(
          `Missing required fields: ${missingFields.join(", ")}`,
          "MISSING_REQUIRED_FIELDS"
        );
      }
  
      if (!data.city || !data.country) {
        console.log('Missing location data:', { city: data.city, country: data.country });
        throw new ProfileServiceError(
          "Location (city and country) is required",
          "MISSING_REQUIRED_FIELDS"
        );
      }
    }
  
    static async createProfile(profile: {
      type: "talent" | "promoter";
      data: TalentFormData | PromoterFormData;
    }): Promise<string> {
      console.log('Creating profile with input:', {
        type: profile.type,
        data: profile.data
      });

      const userId = this.validateAuth();
      console.log('Validated userId:', userId);

      try {
        // Validate form data based on profile type
        if (profile.type === "talent") {
          this.validateTalentFormData(profile.data as TalentFormData);
        } else {
          console.log('Validating promoter data:', profile.data);
          this.validatePromoterFormData(profile.data as PromoterFormData);
        }
  
        // Handle image upload
        let imageUrl: string | undefined;
        if (profile.data.image instanceof File) {
          console.log('Uploading image file');
          imageUrl = await this.handleImageUpload(userId, profile.data.image, profile.type);
          console.log('Image uploaded, URL:', imageUrl);
        }
  
        // Create location
        const location = await LocationService.createLocation({
          city: profile.data.city,
          state: profile.data.state,
          country: profile.data.country,
        });
        console.log('Created location:', location);
  
        // Prepare data for save
        const { image, city, state, country, ...otherFormData } = profile.data;
        console.log('Other form data after destructuring:', otherFormData);
        
        // Transform form data to match database schema
        const transformedData = profile.type === "talent" 
          ? {
              type: (profile.data as TalentFormData).talentType,
              ...(otherFormData as Omit<TalentFormData, 'talentType' | 'image' | 'city' | 'state' | 'country'>)
            }
          : {
              ...(otherFormData as Omit<PromoterFormData, 'image' | 'city' | 'state' | 'country'>)
          };
        
        console.log('Transformed data:', transformedData);
  
        const dataToSave: UserProfile = {
          id: userId,
          type: profile.type,
          [`${profile.type}Data`]: {
            ...transformedData,
            location,
            ...(imageUrl && { imageUrl }),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        };
  
        console.log('Final data to save:', JSON.stringify(dataToSave, null, 2));
  
        await setDoc(doc(db, this.COLLECTION, userId), dataToSave);
        return userId;
      } catch (error) {
        console.error('Error details:', error);
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to create profile",
          "MISSING_PROFILE_DATA",
          error
        );
      }
    }
  
    static async getProfile(id: string): Promise<UserProfile> {
      try {
        const docSnap = await getDoc(doc(db, this.COLLECTION, id));
  
        if (!docSnap.exists()) {
          throw new ProfileServiceError("Profile not found", "PROFILE_NOT_FOUND");
        }
  
        return docSnap.data() as UserProfile;
      } catch (error) {
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to retrieve profile",
          "PROFILE_NOT_FOUND",
          error
        );
      }
    }

    static async updateProfileSection<T extends keyof TalentData>(
        id: string, 
        section: T, 
        data: TalentData[T]
      ): Promise<void> {
        const updateData = {
          [`talentData.${section}`]: data,
          [`talentData.updatedAt`]: serverTimestamp()
        };
        
        await updateDoc(doc(db, this.COLLECTION, id), updateData);
      }
  
    static async updateProfile(
        id: string,
        type: "talent" | "promoter",
        updates: Partial<TalentFormData | PromoterFormData>
      ): Promise<void> {
      
        try {
          
          const currentProfile = await this.getProfile(id);
          
          const profileData =
            type === "talent"
              ? currentProfile.talentData
              : currentProfile.promoterData;
      
      
          if (!profileData) {
            throw new ProfileServiceError(
              "No profile data found",
              "MISSING_PROFILE_DATA"
            );
          }
      
          let imageUrl: string | undefined;
          if ("image" in updates && updates.image instanceof File) {
            imageUrl = await this.handleImageUpload(
              id,
              updates.image,
              type,
              profileData.imageUrl
            );
          }
      
          let locationUpdate = {};
          if (updates.city || updates.state || updates.country) {
            const updatedLocation = await LocationService.updateLocation(
              profileData.location,
              {
                city: updates.city,
                state: updates.state,
                country: updates.country,
              }
            );
            locationUpdate = { location: updatedLocation };
          }
      
          const { image, city, state, country, ...cleanUpdates } = updates;
      
          // Handle talentType update correctly
          let transformedUpdates: Partial<TalentData> = {
            ...cleanUpdates,
          };
      
          if (
            type === "talent" &&
            "talentType" in cleanUpdates &&
            cleanUpdates.talentType !== undefined
          ) {
            transformedUpdates.type = cleanUpdates.talentType as TalentType;
            delete (transformedUpdates as any).talentType;
          }
      
          // Handle username update at the root level and in the specific data object
          const updateData: any = {
            [`${type}Data`]: {
              ...profileData,
              ...transformedUpdates,
              ...locationUpdate,
              ...(imageUrl && { imageUrl }),
              ...(updates.username && { username: updates.username.toLowerCase() }),
              updatedAt: serverTimestamp(),
            }
          };
      
      
          await updateDoc(doc(db, this.COLLECTION, id), updateData);
          
          // Verify the update
          const updatedProfile = await this.getProfile(id);
      
        } catch (error) {
          console.error('ProfileService.updateProfile - Error:', error);
          if (error instanceof ProfileServiceError) throw error;
          throw new ProfileServiceError(
            "Failed to update profile",
            "UPDATE_FAILED",
            error
          );
        }
      }
  
    static async deleteProfile(id: string): Promise<void> {
      try {
        const profile = await this.getProfile(id);
        const profileData = profile.talentData || profile.promoterData;
  
        if (profileData?.imageUrl) {
          const imageRef = ref(storage, profileData.imageUrl);
          await deleteObject(imageRef);
        }
  
        await deleteDoc(doc(db, this.COLLECTION, id));
      } catch (error) {
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to delete profile",
          "DELETE_FAILED",
          error
        );
      }
    }
  
    private static sanitizeFileName(fileName: string): string {
      return fileName
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace any non-alphanumeric characters (except . and -) with _
        .replace(/_{2,}/g, '_'); // Replace multiple consecutive underscores with a single one
    }
  
    private static async handleGalleryImageUpload(
      userId: string,
      file: File,
      oldImageUrl?: string
    ): Promise<string> {
      try {
        if (!file.type.startsWith("image/")) {
          throw new ProfileServiceError(
            "File must be an image",
            "INVALID_FILE_TYPE"
          );
        }
  
        if (file.size > this.MAX_IMAGE_SIZE) {
          throw new ProfileServiceError(
            "Image size must be less than 5MB",
            "FILE_TOO_LARGE"
          );
        }
  
        if (oldImageUrl) {
          try {
            const oldImageRef = ref(storage, oldImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.warn("Failed to delete old gallery image:", error);
          }
        }
  
        const sanitizedFileName = this.sanitizeFileName(file.name);
        const fileName = `${this.GALLERY_PATH}/${userId}/${Date.now()}-${sanitizedFileName}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      } catch (error) {
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to handle gallery image upload",
          "IMAGE_UPLOAD_ERROR",
          error
        );
      }
    }
  
    static async updateGallery(
      userId: string,
      type: "talent" | "promoter",
      newMedia: MediaUpload[],
      oldMedia?: MediaItem[]
    ): Promise<MediaItem[]> {
      try {
        this.validateAuth();
  
        const existingUrls = new Set(oldMedia?.map((item) => item.url) || []);
        const updatedMedia: MediaItem[] = [];
  
        for (const item of newMedia) {
          if (item.file) {
            const url = await this.handleGalleryImageUpload(userId, item.file);
            updatedMedia.push({
              url,
              type: "image",
              caption: item.caption || "",
            });
          } else if (item.url) {
            existingUrls.delete(item.url);
            updatedMedia.push({
              url: item.url,
              type: "image",
              caption: item.caption || "",
            });
          }
        }
  
        // Update Firestore
        const dataPath = `${type}Data.media`;
        await updateDoc(doc(db, this.COLLECTION, userId), {
          [dataPath]: updatedMedia,
        });
  
        // Clean up unused images
        for (const urlToDelete of existingUrls) {
          try {
            const url = new URL(urlToDelete);
            const pathWithPrefix = url.pathname.split('/o/')[1];
            if (!pathWithPrefix) {
              console.warn('Invalid storage URL format:', urlToDelete);
              continue;
            }

            const storagePath = decodeURIComponent(pathWithPrefix);
            
            try {
              const imageRef = ref(storage, storagePath);
              await deleteObject(imageRef);
              // Remove the URL from the set after successful deletion
              existingUrls.delete(urlToDelete);
            } catch (deleteError) {
              if ((deleteError as any)?.code === 'storage/object-not-found') {
                // If the file is already deleted, just remove it from the set
                existingUrls.delete(urlToDelete);
                console.log('File already deleted:', storagePath);
              } else {
                throw deleteError; // Re-throw other errors
              }
            }
          } catch (error) {
            console.error("Failed to delete image from storage:", {
              url: urlToDelete,
              error: error instanceof Error ? error.message : error
            });
          }
        }
  
        return updatedMedia;
      } catch (error) {
        if (error instanceof ProfileServiceError) throw error;
        throw new ProfileServiceError(
          "Failed to update gallery",
          "GALLERY_UPDATE_FAILED",
          error
        );
      }
    }
  
    static async checkProfileExists(
      type: "talent" | "promoter",
      nameField: string
    ): Promise<boolean> {
      try {
        const fieldPath = type === "talent"
          ? "talentData.name"
          : "promoterData.promotionName";
  
        const q = query(
          collection(db, this.COLLECTION),
          where(fieldPath, "==", nameField)
        );
  
        const querySnapshot = await getDocs(q);
        return !querySnapshot.empty;
      } catch (error) {
        throw new ProfileServiceError(
          "Failed to check profile existence",
          "QUERY_FAILED",
          error
        );
      }
    }
  
    public static async isUsernameAvailable(username: string): Promise<{ 
      isAvailable: boolean; 
      isOwnUsername: boolean; 
    }> {
      
      try {
        const db = getFirestore();
        const usersRef = collection(db, 'users');
        const lowercaseUsername = username.toLowerCase();
        
        // Check both promoterData.username and talentData.username
        const promoterQuery = query(
          usersRef, 
          where('promoterData.username', '==', lowercaseUsername)
        );
        
        const talentQuery = query(
          usersRef, 
          where('talentData.username', '==', lowercaseUsername)
        );
        
        const [promoterSnapshot, talentSnapshot] = await Promise.all([
          getDocs(promoterQuery),
          getDocs(talentQuery)
        ]);
        
        const totalMatches = promoterSnapshot.size + talentSnapshot.size;
        const isAvailable = totalMatches === 0;
        
        // If we're updating an existing profile, we need to exclude the current user's document
        const currentUser = auth.currentUser;
        if (!isAvailable && currentUser) {
          const allMatchingDocs = [...promoterSnapshot.docs, ...talentSnapshot.docs];
          const isOwnUsername = allMatchingDocs.length === 1 && 
                               allMatchingDocs[0].id === currentUser.uid;
          
          return {
            isAvailable: false,
            isOwnUsername
          };
        }
        
        return {
          isAvailable,
          isOwnUsername: false
        };
      } catch (error) {
        console.error('Error in isUsernameAvailable:', error);
        throw new ProfileServiceError(
          'Failed to check username availability',
          'QUERY_FAILED',
          error
        );
      }
    }
  
    static async getProfileByUsername(username: string): Promise<UserProfile | null> {
      try {
        
        // Query both promoterData.username and talentData.username
        const [promoterQuery, talentQuery] = await Promise.all([
          getDocs(query(
            collection(db, 'users'),
            where('promoterData.username', '==', username.toLowerCase()),
            limit(1)
          )),
          getDocs(query(
            collection(db, 'users'),
            where('talentData.username', '==', username.toLowerCase()),
            limit(1)
          ))
        ]);


        const doc = promoterQuery.docs[0] || talentQuery.docs[0];
        
        if (!doc) {
          console.log('No profile found for username:', username);
          return null;
        }

        const profile = {
          id: doc.id,
          ...doc.data()
        } as UserProfile;
        
        return profile;
      } catch (error) {
        console.error('Error in getProfileByUsername:', error);
        throw error;
      }
    }
  }